/**
 * Ключ за идемпотентност на действие с НЕИЗВЕСТЕН изход (одит 01.10.2026, N-01).
 *
 * Сервитьорът натиска „Поръчай", отговорът се губи (таймаут, 5xx, връзката пада
 * точно при прехода облак → каса) и той натиска пак. Без ключ второто натискане
 * беше ВТОРА поръчка: втори кухненски билет и двойна сума — онлайн, на касата и
 * когато първото е минало в облака, а повторението — на касата офлайн.
 *
 * Правилото: същото тяло, повторено СЛЕД провал с неизвестен изход, носи
 * същия ключ; успех или категоричен отказ (4xx) го забравят — следващата
 * еднаква поръчка е нова и законна. Ключът е в ТЯЛОТО (`idempotencyKey`), не в
 * хедър: облакът в production пуска през CORS само `Authorization` и
 * `Content-Type`, а непознато поле в тялото всяка версия на облака и на касата
 * пренебрегва. Касата го пази в дневника и той стига до облака с replay-а.
 *
 * Автоматично повторение на мутация НЯМА и сега (`main.tsx`: `retry: 0`,
 * `client.ts` §Повторение след смяна на режима) — ключът пази ръчното.
 *
 * Ключовете стоят в `localStorage` (втори преглед на одита): само в паметта
 * презареден екран — точно това, което човек прави, когато „нещо увисне" —
 * забравяше ключа и повторението ставаше нова поръчка. Хранилището е на
 * устройството (общо за табовете му); друг терминал е друго човешко действие
 * и ключ не го разпознава по замисъл. Без хранилище (частен прозорец, забрана)
 * — само паметта, както преди.
 *
 * ПРИКЛЮЧЕНО ≠ НЕИЗВЕСТНО (одит 0.0.290→0.0.321, R16/R17). Ключът е на
 * конкретно намерение, не на отпечатъка:
 *  • приключването (успех или категоричен отказ) забравя само СВОЯ ключ —
 *    късен отговор на стара заявка X не трие ключа Y на по-новата еднаква
 *    поръчка (R17);
 *  • приключеният ключ се вписва и в списък „приключени" в хранилището. Таб,
 *    който още го помни, го пуска при следващото четене — иначе таб A
 *    възкресяваше ключа, вече приключен в таб B, и BE-то пропускаше новата
 *    еднаква поръчка като повторение (R16);
 *  • ключ, който просто липсва в хранилището (изтрито, пълно, частен прозорец),
 *    НЕ е приключен: неизвестното намерение остава в паметта на таба.
 */

export const IDEMPOTENCY_KEY_FIELD = "idempotencyKey";

/** Колко дълго повторение със същото тяло е „същото действие". */
export const IDEMPOTENCY_RETAIN_MS = 10 * 60_000;
const MAX_REMEMBERED = 200;

/** Действията, чието повторение е второ действие с последици извън базата. */
const IDEMPOTENT_ROUTES: RegExp[] = [
  /^\/bills\/?$/, // поръчка — нова маса или допоръчка (кухненски билет, сума)
  /^\/bills\/[^/]+\/cancel-items?$/, // сторно (storno билет, сума)
  /^\/operations\/cash-(in|out)$/, // служебен внос/износ (пари)
];

interface Remembered {
  key: string;
  expiresAt: number;
}

const STORAGE_KEY = "cloudts.idempotencyKeys.v1";
/** Приключените ключове → докога се помнят (R16). */
const COMPLETED_STORAGE_KEY = "cloudts.idempotencyCompleted.v1";

/** Паметта на ТОЗИ таб — и резервата, когато хранилището не е достъпно. */
const remembered = new Map<string, Remembered>();

/**
 * Приключените ключове → докога важи записът. Помнят се колкото живее и самият
 * ключ (`IDEMPOTENCY_RETAIN_MS`): всяко четене първо слива този списък, затова
 * помнен в друг таб ключ, освежен ПРЕДИ приключването, изтича по-рано от него.
 */
const completed = new Map<string, number>();

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isRemembered(value: unknown): value is Remembered {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Remembered).key === "string" &&
    typeof (value as Remembered).expiresAt === "number"
  );
}

/** Двойките `[низ, стойност]` от хранилището; повредено/липсващо → нищо. */
function readStoredPairs(store: Storage, storageKey: string): Array<[string, unknown]> {
  try {
    const parsed: unknown = JSON.parse(store.getItem(storageKey) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is [string, unknown] => Array.isArray(entry) && typeof entry[0] === "string",
    );
  } catch {
    // Повредено хранилище — паметта е достатъчна, следващият запис го пренаписва.
    return [];
  }
}

/**
 * Записаното от другите табове/преди презареждането влиза в паметта, после
 * приключеното където и да е пада от нея (R16).
 */
function loadStored() {
  const store = storage();
  if (store) {
    for (const [completedKey, expiresAt] of readStoredPairs(store, COMPLETED_STORAGE_KEY)) {
      if (typeof expiresAt !== "number") continue;
      completed.set(completedKey, Math.max(expiresAt, completed.get(completedKey) ?? 0));
    }
    for (const [fingerprint, value] of readStoredPairs(store, STORAGE_KEY)) {
      // Помненият ключ печели: друг таб може вече да е пратил същото тяло с него.
      if (isRemembered(value)) remembered.set(fingerprint, value);
    }
  }
  for (const [fingerprint, entry] of remembered) {
    if (completed.has(entry.key)) remembered.delete(fingerprint);
  }
}

function saveStoredPairs(store: Storage, storageKey: string, pairs: Map<string, unknown>) {
  if (pairs.size === 0) store.removeItem(storageKey);
  else store.setItem(storageKey, JSON.stringify([...pairs]));
}

function saveStored() {
  const store = storage();
  if (!store) return;
  try {
    saveStoredPairs(store, COMPLETED_STORAGE_KEY, completed);
    saveStoredPairs(store, STORAGE_KEY, remembered);
  } catch {
    // Пълно/забранено хранилище — остава паметта.
  }
}

function pathOf(url: string | undefined): string {
  const raw = url ?? "";
  const withoutQuery = raw.split("?")[0];
  // Абсолютен адрес (рядко) → само пътят.
  return withoutQuery.replace(/^[a-z]+:\/\/[^/]+/i, "");
}

export function isIdempotentMutation(
  method: string | undefined,
  url: string | undefined,
): boolean {
  if ((method ?? "get").toLowerCase() !== "post") return false;
  const path = pathOf(url);
  return IDEMPOTENT_ROUTES.some((route) => route.test(path));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/** JSON с подредени ключове — едно и също тяло дава един и същ отпечатък. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .filter((key) => key !== IDEMPOTENCY_KEY_FIELD && value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

export function fingerprintOf(url: string | undefined, data: unknown): string {
  return `${pathOf(url)}|${stableStringify(data)}`;
}

/** uuid v4 — `randomUUID` го няма извън secure context (касата по LAN през http). */
export function newUuid(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  // uuid v4 от случайни байтове — за среда без `randomUUID`.
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function pruneMap<Value>(entries: Map<string, Value>, isExpired: (value: Value) => boolean) {
  for (const [name, value] of entries) {
    if (isExpired(value)) entries.delete(name);
  }
  while (entries.size > MAX_REMEMBERED) {
    const oldest = entries.keys().next().value;
    if (oldest === undefined) break;
    entries.delete(oldest);
  }
}

function prune(now: number) {
  pruneMap(remembered, (entry) => entry.expiresAt <= now);
  pruneMap(completed, (expiresAt) => expiresAt <= now);
}

/**
 * Ключът за това тяло: помненият (повторение след провал с неизвестен изход
 * или двойно натискане, докато първото още е във въздуха) или нов.
 */
export function keyFor(fingerprint: string, now: number = Date.now()): string {
  loadStored();
  prune(now);
  const existing = remembered.get(fingerprint);
  const key = existing ? existing.key : newUuid();
  remembered.set(fingerprint, { key, expiresAt: now + IDEMPOTENCY_RETAIN_MS });
  saveStored();
  return key;
}

/**
 * Изходът на заявката с ключ `key` е КАТЕГОРИЧЕН (успех или отказ на сървъра):
 * ключът се отбелязва като приключен за всички табове, а отпечатъкът се
 * забравя САМО ако още е на този ключ (R17) — по-новото намерение със същото
 * тяло си пази своя ключ.
 */
export function forgetKey(
  fingerprint: string | undefined,
  key: string | undefined,
  now: number = Date.now(),
) {
  if (!fingerprint || !key) return;
  loadStored();
  prune(now);
  completed.delete(key); // освежен се мести най-отзад — пада последен при препълване
  completed.set(key, now + IDEMPOTENCY_RETAIN_MS);
  if (remembered.get(fingerprint)?.key === key) remembered.delete(fingerprint);
  saveStored();
}

/** Само за тестове: паметта И хранилището. */
export function resetIdempotencyKeysForTests() {
  remembered.clear();
  completed.clear();
  try {
    storage()?.removeItem(STORAGE_KEY);
    storage()?.removeItem(COMPLETED_STORAGE_KEY);
  } catch {
    // няма хранилище
  }
}

/** Само за тестове: „презареждане" — паметта на таба изчезва, хранилището остава. */
export function forgetTabMemoryForTests() {
  remembered.clear();
  completed.clear();
}

export { isPlainObject as isPlainBody };
